/**
 * SimpleCbSheet — generic estimate-scoped worksheet.
 *
 * One component powers every "flat table" sheet of the Cost Breakdown
 * (Indirects, Site Expenses, Crew Calcs, Clarifications, SOV / Client WBS):
 * same editing, library import, export, and help behaviour everywhere.
 * Configure via the SHEETS map at the bottom or pass a custom config.
 *
 * Modes:
 *   estimate provided -> edits that estimate's rows, can import from library
 *   no estimate       -> LIBRARY MODE: edits the shared reference library
 *                        (rows with estimate_id NULL) that every estimate
 *                        imports from
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCbApi } from './cbApi';
import {
  SheetShell, HeaderButton, ActionButton, EditableTable, HelpModal, LibraryPickerModal,
  LoadingState, ErrorState, EmptyState, NoEstimateState, useToast, downloadExport, fmt,
} from './CbUi';
import CbAuditLogButton from './CbAuditLogButton';

const SimpleCbSheet = ({ estimate, sheet }) => {
  const {
    resource, title, icon, subtitle, accent, columns, newRow,
    hasLibrary = true, kpis: kpiDefs = [], searchKeys = [], helpId, help,
    libraryColumns, libraryFilterColumn, auditTable,
    syncAction, // { label, endpoint, buildBody?, confirm? }
  } = sheet;

  const api = useCbApi();
  const { showToast, ToastEl } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sovRollupByCode, setSovRollupByCode] = useState({});

  // Without an estimate we edit the shared reference library itself
  const libraryMode = !estimate?.id;

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const scope = libraryMode ? 'library=1' : `estimate_id=${estimate.id}`;
      const res = await api.get(`/cost-breakdown/${resource}?${scope}`);
      let next = res.data?.data || [];

      // SOV: overlay live SUMIFS rollup onto each WBS line
      if (!libraryMode && resource === 'client-wbs' && estimate?.id) {
        try {
          const roll = await api.get(`/cost-breakdown/client-wbs/rollup/${estimate.id}`);
          const byCode = roll.data?.byCode || {};
          setSovRollupByCode(byCode);
          next = next.map((r) => {
            const code = String(r.wbs_code || '').trim();
            const b = byCode[code] || r.discipline_allocations || {};
            return {
              ...r,
              labour_cost: b.labour ?? 0,
              material_cost: b.material ?? 0,
              total_cost: b.total ?? ((Number(b.labour) || 0) + (Number(b.material) || 0) + (Number(b.subs) || 0) + (Number(b.rentals) || 0)),
            };
          });
        } catch {
          setSovRollupByCode({});
        }
      } else {
        setSovRollupByCode({});
      }

      setRows(next);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id, libraryMode, resource]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const term = search.toLowerCase();
    const keys = searchKeys.length ? searchKeys : columns.slice(0, 5).map((c) => c.key);
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, search, searchKeys, columns]);

  const kpiCards = useMemo(() => {
    const sum = (key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    return [
      { label: 'Rows', value: rows.length, icon: '📋', color: accent },
      ...kpiDefs.map((k) => ({
        label: k.label, icon: k.icon, color: k.color,
        value: fmt(sum(k.key), k.type || 'currency'),
      })),
    ];
  }, [rows, kpiDefs, accent]);

  const handleCellSave = useCallback(async (row, key, value) => {
    try {
      const res = await api.put(`/cost-breakdown/${resource}/${row.id}`, { [key]: value });
      const updated = res.data?.data;
      if (updated) setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      else fetchRows();
    } catch (err) {
      showToast(`Update failed: ${err.message}`, 'error');
    }
  }, [api, resource, fetchRows, showToast]);

  const handleAddRow = useCallback(async () => {
    try {
      const res = await api.post(`/cost-breakdown/${resource}`, {
        ...(libraryMode ? {} : { estimate_id: estimate.id }),
        sort_order: (rows.length + 1) * 10,
        ...(typeof newRow === 'function' ? newRow(rows) : newRow || {}),
      });
      const created = res.data?.data;
      if (created) setRows((prev) => [...prev, created]);
      showToast('Row added — click any cell to edit');
    } catch (err) {
      showToast(`Failed to add row: ${err.message}`, 'error');
    }
  }, [api, resource, estimate?.id, libraryMode, rows, newRow, showToast]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected row(s)?`)) return;
    try {
      await api.post(`/cost-breakdown/${resource}/bulk-delete`, { ids: [...selectedRows] });
      setRows((prev) => prev.filter((r) => !selectedRows.has(r.id)));
      setSelectedRows(new Set());
      showToast('Deleted');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  }, [api, resource, selectedRows, showToast]);

  const handleExport = useCallback(async () => {
    try {
      const scope = libraryMode ? 'library=1' : `estimate_id=${estimate.id}`;
      const stem = libraryMode ? `${resource}_library` : `${estimate.estimate_number}_${resource}`;
      await downloadExport(api, `/cost-breakdown/${resource}/export?${scope}`, `${stem}.xlsx`);
      showToast('Exported');
    } catch (err) {
      showToast(`Export failed: ${err.message}`, 'error');
    }
  }, [api, resource, estimate, libraryMode, showToast]);

  const handleSuggestIndirects = useCallback(async (apply) => {
    if (!estimate?.id) return;
    setSuggesting(true);
    try {
      const res = await api.post('/cost-breakdown/indirects/suggest', {
        estimate_id: estimate.id,
        apply,
      });
      if (apply) {
        showToast(`Applied ${res.data?.inserted?.length || 0} suggested indirect rows`);
        fetchRows();
      } else {
        const s = res.data?.suggestions || [];
        const msg = s.map((x) => `${x.position || x.description}: ${fmt(x.grand_total_cost, 'currency')}`).join(' · ');
        showToast(msg || 'No suggestions');
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.message, 'error');
    } finally {
      setSuggesting(false);
    }
  }, [api, estimate?.id, fetchRows, showToast]);

  const handleSyncAction = useCallback(async () => {
    if (!estimate?.id || !syncAction) return;
    if (syncAction.confirm && !window.confirm(syncAction.confirm)) return;
    setSyncing(true);
    try {
      const body = typeof syncAction.buildBody === 'function'
        ? syncAction.buildBody(estimate)
        : { estimate_id: estimate.id };
      const path = typeof syncAction.endpoint === 'function'
        ? syncAction.endpoint(estimate)
        : syncAction.endpoint;
      const res = await api.post(`/cost-breakdown/${path}`, body);
      showToast(res.data?.message || 'Synced');
      fetchRows();
    } catch (err) {
      showToast(err.response?.data?.message || err.message, 'error');
    } finally {
      setSyncing(false);
    }
  }, [api, estimate, syncAction, fetchRows, showToast]);

  if (libraryMode && !hasLibrary) return <NoEstimateState section={title} />;
  if (loading && rows.length === 0) return <LoadingState label={`Loading ${title}…`} />;
  if (error) return <ErrorState error={error} onRetry={fetchRows} />;

  return (
    <>
      <SheetShell
        title={libraryMode ? `📚 ${title} — Library` : `${icon} ${title}`}
        subtitle={libraryMode
          ? `Shared reference library — every estimate imports its ${title.toLowerCase()} from here`
          : `Estimate ${estimate.estimate_number} · ${estimate.estimate_name || ''}${subtitle ? ` · ${subtitle}` : ''}`}
        gradient={libraryMode ? 'linear-gradient(135deg, #475569, #64748b)' : `linear-gradient(135deg, ${accent}, ${accent}cc)`}
        headerActions={
          <>
            {resource === 'indirects' && !libraryMode && (
              <>
                <HeaderButton onClick={() => handleSuggestIndirects(false)} disabled={suggesting}>
                  {suggesting ? '…' : '💡 Preview suggestions'}
                </HeaderButton>
                <HeaderButton onClick={() => handleSuggestIndirects(true)} disabled={suggesting}>
                  ✨ Apply auto-indirects
                </HeaderButton>
              </>
            )}
            {syncAction && !libraryMode && (
              <HeaderButton onClick={handleSyncAction} disabled={syncing} title={syncAction.title || syncAction.label}>
                {syncing ? '⏳ Syncing…' : syncAction.label}
              </HeaderButton>
            )}
            {hasLibrary && !libraryMode && <HeaderButton onClick={() => setShowLibrary(true)}>📚 Import from Library</HeaderButton>}
            <HeaderButton onClick={handleExport}>📥 Export</HeaderButton>
            <CbAuditLogButton
              tableName={auditTable || `cb_${resource.replace(/-/g, '_')}`}
              estimateId={libraryMode ? null : estimate?.id}
              title={`${title} Audit Log`}
              accentColor={accent}
            />
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={kpiCards}
        toolbar={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: '1 1 240px', maxWidth: 380, padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}
            />
            <ActionButton color={accent} onClick={handleAddRow}>+ Add Row</ActionButton>
            {selectedRows.size > 0 && (
              <ActionButton color="#dc2626" onClick={handleBulkDelete}>🗑️ Delete ({selectedRows.size})</ActionButton>
            )}
          </div>
        }
      >
        {rows.length === 0 ? (
          <EmptyState icon={icon} title={libraryMode ? `${title} library is empty` : `No ${title} rows yet`}>
            <p>
              {libraryMode
                ? <>Add the standard reference rows here once — every estimate can then <strong>import</strong> them.</>
                : hasLibrary
                  ? <>Use <strong>Import from Library</strong> to pull in the standard reference rows, or <strong>+ Add Row</strong> to start from scratch.</>
                  : <>Click <strong>+ Add Row</strong> to add the first row.</>}
            </p>
          </EmptyState>
        ) : (
          <EditableTable
            columns={columns}
            rows={filteredRows}
            onCellSave={handleCellSave}
            selectedRows={selectedRows}
            onToggleRow={(id) => setSelectedRows((prev) => {
              const next = new Set(prev);
              next.has(id) ? next.delete(id) : next.add(id);
              return next;
            })}
            onToggleAll={() => setSelectedRows((prev) =>
              prev.size === filteredRows.length ? new Set() : new Set(filteredRows.map((r) => r.id))
            )}
            accent={accent}
          />
        )}
        {ToastEl}
      </SheetShell>

      {hasLibrary && !libraryMode && (
        <LibraryPickerModal
          isOpen={showLibrary}
          onClose={() => setShowLibrary(false)}
          onImported={(count) => { showToast(`Imported ${count} rows from library`); fetchRows(); }}
          resource={resource}
          estimateId={estimate.id}
          title={`${title} Library`}
          accent={accent}
          filterColumn={libraryFilterColumn}
          displayColumns={libraryColumns || columns.slice(0, 7)}
        />
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={`${title} Help`} accent={accent} guideId={helpId}>
        {help}
        <h4>Working the sheet</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>Click</strong> any cell to edit; Enter saves, Escape cancels.</li>
          {libraryMode ? (
            <li>You are editing the <strong>shared library</strong> (no estimate selected). These rows are the reference set every estimate can import — changing them does NOT change estimates that already imported copies.</li>
          ) : (
            hasLibrary && <li><strong>Import from Library</strong> copies shared reference rows into this estimate — you can then tailor them freely without touching the library.</li>
          )}
          {!libraryMode && <li>Everything here feeds the <strong>CB Summary</strong> one-pager live.</li>}
        </ul>
      </HelpModal>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sheet configurations
// ---------------------------------------------------------------------------

export const SHEETS = {
  indirects: {
    resource: 'indirects',
    auditTable: 'cb_indirects',
    title: 'Indirects',
    icon: '👥',
    subtitle: 'indirect labour: staff, supervision, vehicles',
    accent: '#7c3aed',
    helpId: 'cb-indirects-help',
    searchKeys: ['position', 'name', 'discipline', 'section', 'notes'],
    kpis: [
      { key: 'grand_total_hours', label: 'Total Hours', icon: '⏱️', color: '#059669', type: 'number' },
      { key: 'grand_total_cost', label: 'Total Cost', icon: '💰', color: '#dc2626' },
      { key: 'grand_total_rnb_cost', label: 'R&B Cost', icon: '🏨', color: '#ea580c' },
      { key: 'vehicle_total_cost', label: 'Vehicles', icon: '🚗', color: '#0891b2' },
    ],
    newRow: { section: 'Staff', position: 'New position' },
    libraryFilterColumn: 'section',
    columns: [
      { key: 'section', label: 'Section', type: 'text', width: 100 },
      { key: 'shift', label: 'Shift', type: 'text', width: 80 },
      { key: 'position', label: 'Position', type: 'text', width: 180 },
      { key: 'name', label: 'Name', type: 'text', width: 140 },
      { key: 'discipline', label: 'Discipline', type: 'text', width: 110 },
      { key: 'labour_type', label: 'Labour Type', type: 'text', width: 130 },
      { key: 'pre_site_weeks', label: 'Pre Wks', type: 'number', width: 80 },
      { key: 'pre_site_hrs_per_week', label: 'Pre Hrs/Wk', type: 'number', width: 90 },
      { key: 'pre_site_blended_rate', label: 'Pre Rate', type: 'currency', width: 95 },
      { key: 'site_weeks', label: 'Site Wks', type: 'number', width: 80 },
      { key: 'site_hrs_per_week', label: 'Site Hrs/Wk', type: 'number', width: 95 },
      { key: 'site_blended_rate', label: 'Site Rate', type: 'currency', width: 95 },
      { key: 'post_site_weeks', label: 'Post Wks', type: 'number', width: 80 },
      { key: 'post_site_hrs_per_week', label: 'Post Hrs/Wk', type: 'number', width: 95 },
      { key: 'post_site_blended_rate', label: 'Post Rate', type: 'currency', width: 95 },
      { key: 'grand_total_hours', label: 'Total Hrs', type: 'number', width: 95 },
      { key: 'grand_total_cost', label: 'Total Cost', type: 'currency', width: 110 },
      { key: 'grand_total_rnb_cost', label: 'Total R&B', type: 'currency', width: 105 },
      { key: 'vehicle_qty', label: 'Veh Qty', type: 'number', width: 75 },
      { key: 'vehicle_months', label: 'Veh Months', type: 'number', width: 90 },
      { key: 'vehicle_cost_per_month', label: 'Veh $/Mo', type: 'currency', width: 95 },
      { key: 'vehicle_total_cost', label: 'Veh Total', type: 'currency', width: 100 },
      { key: 'notes', label: 'Notes', type: 'text', width: 180 },
    ],
    help: (
      <>
        <p><strong>Indirect labour</strong> is everyone who supports the job without being direct scope hours: project managers, coordinators, QC, safety, plus their vehicles.</p>
        <p>Each row covers a position across three phases — <strong>pre-site, site, post-site</strong> — with weeks × hours/week × blended rate. The grand totals feed the Indirects line of the CB Summary.</p>
      </>
    ),
  },

  siteExpenses: {
    resource: 'site-expenses',
    auditTable: 'cb_site_expenses',
    title: 'Site Expenses',
    icon: '🏗️',
    subtitle: 'trailers, tool crib, consumables, site services',
    accent: '#6366f1',
    helpId: 'site-expenses-detail-help',
    searchKeys: ['description', 'trade', 'comments'],
    kpis: [
      { key: 'total', label: 'Total Cost', icon: '💰', color: '#dc2626' },
    ],
    newRow: { description: 'New expense', qty: 1 },
    libraryFilterColumn: 'trade',
    columns: [
      { key: 'description', label: 'Description', type: 'text', width: 260 },
      { key: 'qty', label: 'Qty', type: 'number', width: 70 },
      { key: 'period', label: 'Period', type: 'text', width: 90 },
      { key: 'duration', label: 'Duration', type: 'number', width: 90 },
      { key: 'rate', label: 'Rate', type: 'currency', width: 100 },
      { key: 'total', label: 'Total', type: 'currency', width: 110 },
      { key: 'trade', label: 'Trade', type: 'text', width: 110 },
      { key: 'comments', label: 'Comments', type: 'text', width: 220 },
    ],
    help: (
      <>
        <p><strong>Site expenses</strong> are the recurring facility costs of running the job: office trailers, tool cribs, consumable distribution, waste, utilities.</p>
        <p>Total = Qty × Duration × Rate (enter the computed total). The sheet total flows into the Site Expenses line of the CB Summary.</p>
      </>
    ),
  },

  crew: {
    resource: 'crew-calculation',
    auditTable: 'cb_crew_calculation',
    title: 'Crew Calculations',
    icon: '👷',
    subtitle: 'crew size ↔ duration what-ifs per discipline',
    accent: '#0891b2',
    helpId: 'crew-calculation-bank-help',
    searchKeys: ['discipline', 'notes'],
    syncAction: {
      label: '⬆️ Pull MH from Summaries',
      title: 'Set man-hours from Direct/Other Direct/Supervisory/Support labour on Discipline Summaries (idempotent AUTO rows)',
      endpoint: 'crew-calculation/pull-from-summary',
      confirm: 'Pull man-hours from Discipline Summaries into Crew Calcs? Existing AUTO-from-summary rows will be updated; your crew size / schedule edits are kept.',
    },
    kpis: [
      { key: 'man_hours', label: 'Man Hours', icon: '⏱️', color: '#059669', type: 'number' },
    ],
    newRow: { discipline: 'Piping', tot_hrs_per_week: 50, nb_days_per_week: 5, hrs_per_day: 10 },
    libraryFilterColumn: 'discipline',
    columns: [
      { key: 'discipline', label: 'Discipline', type: 'text', width: 130 },
      { key: 'man_hours', label: 'Man Hours', type: 'number', width: 100 },
      { key: 'man_days', label: 'Man Days', type: 'number', width: 95 },
      { key: 'man_weeks', label: 'Man Weeks', type: 'number', width: 95 },
      { key: 'man_months', label: 'Man Months', type: 'number', width: 100 },
      { key: 'tot_hrs_per_week', label: 'Hrs/Week', type: 'number', width: 90 },
      { key: 'nb_days_per_week', label: 'Days/Week', type: 'number', width: 90 },
      { key: 'hrs_per_day', label: 'Hrs/Day', type: 'number', width: 85 },
      { key: 'crew_size', label: 'Crew Size', type: 'number', width: 90 },
      { key: 'duration_days', label: 'Duration (d)', type: 'number', width: 100 },
      { key: 'duration_weeks', label: 'Duration (wk)', type: 'number', width: 105 },
      { key: 'duration_months', label: 'Duration (mo)', type: 'number', width: 105 },
      { key: 'notes', label: 'Notes', type: 'text', width: 200 },
    ],
    help: (
      <>
        <p><strong>Crew calculations</strong> answer "how many people for how long?" — given the discipline's man-hours and a work schedule, each row is one crew-size scenario with the resulting duration.</p>
        <p>Use it to sanity-check the schedule against the estimated hours before committing to a bid duration.</p>
      </>
    ),
  },

  clarifications: {
    resource: 'clarifications',
    auditTable: 'cb_clarifications',
    title: 'Clarifications',
    icon: '📝',
    subtitle: 'assumptions, inclusions & exclusions for the bid',
    accent: '#b45309',
    helpId: 'clarifications-help',
    searchKeys: ['description', 'discipline', 'comment', 'item_number'],
    kpis: [],
    newRow: { description: 'New clarification' },
    libraryFilterColumn: 'discipline',
    columns: [
      { key: 'item_number', label: 'Item #', type: 'text', width: 80 },
      { key: 'discipline', label: 'Discipline', type: 'text', width: 120 },
      { key: 'description', label: 'Clarification', type: 'text', width: 440 },
      { key: 'comment', label: 'Comment', type: 'text', width: 240 },
    ],
    help: (
      <>
        <p><strong>Clarifications</strong> are the fine print of the bid: what's assumed, included, and excluded. They protect the estimate when scope questions come up later.</p>
        <p>The library holds LGL's standard clarifications — import the applicable ones, then tailor the wording per estimate.</p>
      </>
    ),
  },

  sov: {
    resource: 'client-wbs',
    auditTable: 'cb_client_wbs',
    title: 'Client WBS / SOV',
    icon: '🗂️',
    subtitle: 'client format — live SUMIFS from detail/summary WBS & SOV tags',
    accent: '#334155',
    helpId: 'cb-sov-help',
    hasLibrary: false,
    searchKeys: ['wbs_code', 'description'],
    syncAction: {
      label: '🔄 Sync from Detail WBS',
      title: 'SUMIFS labour/material from Detailed Estimation (and tagged summary rows) into SOV allocations; creates missing WBS lines',
      endpoint: (est) => `client-wbs/sync-from-detail/${est.id}`,
      buildBody: () => ({ createMissing: true }),
      confirm: 'Sync SOV from detail/summary WBS & SOV tags? Matching lines update; missing WBS codes are created.',
    },
    kpis: [
      { key: 'labour_cost', label: 'Labour', icon: '👷', color: '#2563eb' },
      { key: 'material_cost', label: 'Material', icon: '📦', color: '#d97706' },
      { key: 'total_cost', label: 'Total', icon: '💰', color: '#dc2626' },
    ],
    newRow: { wbs_code: '', description: 'New SOV line' },
    columns: [
      { key: 'wbs_code', label: 'WBS Code', type: 'text', width: 130 },
      { key: 'description', label: 'Description', type: 'text', width: 320 },
      { key: 'labour_cost', label: 'Labour $', type: 'currency', width: 120, computed: true },
      { key: 'material_cost', label: 'Material $', type: 'currency', width: 120, computed: true },
      { key: 'total_cost', label: 'Total $', type: 'currency', width: 120, computed: true },
    ],
    help: (
      <>
        <p><strong>The Schedule of Values</strong> is the <em>client's</em> format — not another LGL cost sheet. Tag <strong>WBS Code</strong> / <strong>SOV Code</strong> on Detailed Estimation (and optional summary rows), then <strong>Sync from Detail WBS</strong> to SUMIFS labour and material onto each client line.</p>
        <p>Labour / Material / Total columns are computed live from those tags. Enter the client's WBS codes here first (or let sync create missing codes).</p>
      </>
    ),
  },
};

export default SimpleCbSheet;
